import React, { useEffect, useMemo, useState } from "react";
import EditAdherentModal from "./../ManageMembers/EditAdherentModal";
import ManageAdherentGroupsModal from "../ManageMembers/ManageAdherentGroupsModal";
import {
  FaEnvelope,
  FaPhone,
  FaSms,
  FaTimes,
  FaUserEdit,
  FaUsers,
  FaMapMarkerAlt,
  FaBirthdayCake,
  FaStickyNote,
} from "react-icons/fa";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function getStatusBadgeClass(status) {
  switch (status) {
    case "Inscrit":
      return "bg-yellow-500 text-white";
    case "Cours d'Essai":
      return "bg-pink-500 text-white";
    case "Licencié":
      return "bg-green-700 text-white";
    case "Licencié hors club":
      return "bg-green-500 text-white";
    case "Non inscrit":
      return "bg-purple-500 text-white";
    case "Arrêt":
      return "bg-slate-700 text-white";
    default:
      return "bg-slate-400 text-white";
  }
}

function formatDate(dateValue) {
  if (!dateValue) return "Non renseignée";

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "Non renseignée";

  return date.toLocaleDateString("fr-FR");
}

function getAdherentImage(adherent) {
  if (adherent?.photo_url) return adherent.photo_url;
  if (adherent?.genre === "Masculin") return "/img/judoka_avatar.png";
  if (adherent?.genre === "Féminin") return "/img/judokate_avatar.png";
  return "/img/default-avatar.png";
}

const AdherentInfoModal = ({
  isOpen,
  onClose,
  adherent,
  onUpdate,
  adherentGroups = [],
  clubId,
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [adherentData, setAdherentData] = useState(adherent);

  useEffect(() => {
    setAdherentData(adherent || null);
  }, [adherent]);

  const displayGroups = useMemo(() => {
    if (Array.isArray(adherentData?.Groups) && adherentData.Groups.length > 0) {
      return adherentData.Groups;
    }
    return Array.isArray(adherentGroups) ? adherentGroups : [];
  }, [adherentData, adherentGroups]);

  const hasChildModalOpen = isEditModalOpen || isGroupModalOpen;

  if (!isOpen || !adherentData) return null;

  const imageSrc = getAdherentImage(adherentData);

  const handleEditUpdate = async (updatedData) => {
    const mergedAdherent = {
      ...adherentData,
      ...updatedData,
      Groups:
        updatedData?.Groups ||
        adherentData?.Groups ||
        (Array.isArray(displayGroups) ? displayGroups : []),
    };

    setAdherentData(mergedAdherent);
    setIsEditModalOpen(false);

    if (onUpdate) {
      await onUpdate(mergedAdherent);
    }
  };

  const handleGroupsUpdate = async (updatedGroups) => {
    const updatedAdherent = {
      ...adherentData,
      Groups: Array.isArray(updatedGroups) ? updatedGroups : [],
    };

    setAdherentData(updatedAdherent);
    setIsGroupModalOpen(false);

    if (onUpdate) {
      await onUpdate(updatedAdherent);
    }
  };

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-[40] bg-slate-950/70 backdrop-blur-sm transition",
          hasChildModalOpen && "pointer-events-none opacity-0",
        )}
      >
        <div className="flex h-[100dvh] items-stretch justify-center p-0 sm:p-4">
          <div className="flex h-[100dvh] w-full max-w-5xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900 sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:rounded-[28px] sm:border sm:border-white/10 dark:sm:border-slate-700">
            {/* Header */}
            <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-4 dark:border-slate-800 dark:bg-slate-900 sm:px-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400">
                    Adhérent
                  </p>
                  <h2 className="mt-1 text-xl font-black text-slate-900 dark:text-slate-100 sm:text-2xl">
                    Fiche adhérent
                  </h2>
                </div>

                <button
                  onClick={onClose}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="grid min-h-full grid-cols-1 lg:grid-cols-[0.95fr_1.05fr]">
                {/* Colonne gauche */}
                <div className="border-b border-white/10 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 p-5 text-white lg:border-b-0 lg:border-r lg:border-white/10 sm:p-6">
                  <p className="inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                    Aperçu
                  </p>

                  <div className="mt-6 flex flex-col items-center rounded-[24px] border border-white/10 bg-white/10 p-4 text-center">
                    <img
                      src={imageSrc}
                      alt={`${adherentData.prenom || ""} ${adherentData.nom || ""}`}
                      className="h-28 w-28 rounded-full object-cover shadow-xl ring-4 ring-white/10 sm:h-32 sm:w-32"
                    />

                    <h3 className="mt-4 text-xl font-black sm:text-2xl">
                      {adherentData.prenom} {adherentData.nom}
                    </h3>

                    <span
                      className={cn(
                        "mt-3 inline-flex rounded-full px-3 py-1 text-xs font-bold",
                        getStatusBadgeClass(adherentData.statut_inscription),
                      )}
                    >
                      {adherentData.statut_inscription || "Statut inconnu"}
                    </span>

                    <div className="mt-5 flex flex-wrap justify-center gap-2">
                      {displayGroups.length > 0 ? (
                        displayGroups.map((group) => (
                          <span
                            key={group.id}
                            className="inline-flex rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white"
                          >
                            {group.nom_groupe}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-white/70">
                          Aucun groupe
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-1">
                    {adherentData.tel && (
                      <a
                        href={`tel:${adherentData.tel}`}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-green-600 px-4 py-3 font-semibold text-white transition hover:bg-green-700"
                      >
                        <FaPhone />
                        Appeler
                      </a>
                    )}

                    {adherentData.tel && (
                      <a
                        href={`sms:${adherentData.tel}`}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 font-semibold text-white transition hover:bg-blue-700"
                      >
                        <FaSms />
                        SMS
                      </a>
                    )}

                    {adherentData.email && (
                      <a
                        href={`mailto:${adherentData.email}`}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-orange-500 px-4 py-3 font-semibold text-white transition hover:bg-orange-600"
                      >
                        <FaEnvelope />
                        Email
                      </a>
                    )}
                  </div>
                </div>

                {/* Colonne droite */}
                <div className="p-4 sm:p-6">
                  <div className="space-y-4">
                    <section className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-800/60">
                      <h3 className="text-lg font-black text-slate-900 dark:text-slate-100">
                        Informations détaillées
                      </h3>

                      <div className="mt-4 space-y-4">
                        <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                          <div className="flex items-start gap-3">
                            <FaMapMarkerAlt className="mt-1 text-slate-400" />
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                Adresse
                              </p>
                              <p className="mt-1 text-sm text-slate-800 dark:text-slate-200">
                                {adherentData.adresse
                                  ? `${adherentData.adresse}, ${adherentData.cp || ""} ${adherentData.commune || ""}`.trim()
                                  : "Non renseignée"}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                          <div className="flex items-start gap-3">
                            <FaBirthdayCake className="mt-1 text-slate-400" />
                            <div>
                              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                Date de naissance
                              </p>
                              <p className="mt-1 text-sm text-slate-800 dark:text-slate-200">
                                {formatDate(adherentData.date_naissance)}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                              Téléphone
                            </p>
                            <p className="mt-1 text-sm text-slate-800 dark:text-slate-200">
                              {adherentData.tel || "Non renseigné"}
                            </p>
                          </div>

                          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                              Email
                            </p>
                            <p className="mt-1 break-words text-sm text-slate-800 dark:text-slate-200">
                              {adherentData.email || "Non renseigné"}
                            </p>
                          </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                          <div className="flex items-start gap-3">
                            <FaUsers className="mt-1 text-slate-400" />
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                Groupes
                              </p>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {displayGroups.length > 0 ? (
                                  displayGroups.map((group) => (
                                    <span
                                      key={group.id}
                                      className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                                    >
                                      {group.nom_groupe}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-sm text-slate-500 dark:text-slate-400">
                                    Aucun groupe
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {adherentData.notes && (
                          <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                            <div className="flex items-start gap-3">
                              <FaStickyNote className="mt-1 text-slate-400" />
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                  Notes
                                </p>
                                <p className="mt-1 whitespace-pre-line text-sm text-slate-800 dark:text-slate-200">
                                  {adherentData.notes}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </section>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="shrink-0 border-t border-slate-200 bg-white px-4 py-4 dark:border-slate-800 dark:bg-slate-900 sm:px-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 font-semibold text-white transition hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                >
                  <FaUserEdit />
                  Modifier
                </button>

                <button
                  onClick={() => setIsGroupModalOpen(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-purple-600 px-5 py-3 font-semibold text-white transition hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600"
                >
                  <FaUsers />
                  Changer de groupe
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <EditAdherentModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        adherent={adherentData}
        onUpdate={handleEditUpdate}
      />

      <ManageAdherentGroupsModal
        isOpen={isGroupModalOpen}
        onClose={() => setIsGroupModalOpen(false)}
        adherent={adherentData}
        adherentGroups={adherentData?.Groups || displayGroups}
        clubId={clubId}
        onUpdate={handleGroupsUpdate}
      />
    </>
  );
};

export default AdherentInfoModal;
